/**
 * RoomSetup.tsx —— 多人对战二级入口：创建房间 / 输入房间号加入。
 *
 * 创建：可配置座位上限 / 盲注 / 带入 / 行动时限，提交后由服务端生成 6 位房间号。
 * 加入：输入 6 位房间号，前端做位数校验，逻辑错误（不存在/已满/进行中）由服务端回发。
 */

import { useEffect, useRef, useState } from 'react';
import {
  mpClient, prewarmWorker, MP_CONNECT_STEPS, mpConnectStageText, type MpState, type MpConnectStage,
} from '../../net/mpClient';
import { hardRefresh } from '../../net/appVersion';
import { Segmented } from '../primitives';

const NAME_KEY = 'riverside.mp.name';
function loadName(): string { try { return localStorage.getItem(NAME_KEY) ?? ''; } catch { return ''; } }
function saveName(n: string): void { try { localStorage.setItem(NAME_KEY, n); } catch { /* noop */ } }

export function RoomSetup({ state, onBack }: { state: MpState; onBack: () => void }) {
  const [tab, setTab] = useState<'create' | 'join'>('create');
  const [name, setName] = useState(loadName());
  const [code, setCode] = useState('');
  const [maxSeats, setMaxSeats] = useState(6);
  const [bb, setBb] = useState(20);
  const [stack, setStack] = useState(1000);
  const [timeout, setTimeoutSec] = useState(30);
  const codeRef = useRef<HTMLInputElement | null>(null);

  // 遗留项 L3/L4：进入入口即预热 Worker / DO，缩短首连等待（fire-and-forget）
  useEffect(() => { prewarmWorker(); }, []);
  // 遗留项 L5：切到「加入房间」时自动聚焦房号输入框，移动端可直接唤起数字键盘
  useEffect(() => { if (tab === 'join') codeRef.current?.focus(); }, [tab]);

  const connecting = state.conn === 'connecting';
  // 第 19 轮：首连二级进度（唤醒房间服务 → 建立连接 → 同步房间信息）
  const stage = state.connectStage;
  const btnLabel = (fallback: string) => (connecting ? mpConnectStageText(stage) : fallback);
  // 连接类失败（线路不通 / 超时）→ 展示旧缓存自救入口；业务类错误（房号无效等）不展示
  const isConnFail = state.fatalCode === 'ws_failed' || state.fatalCode === 'ws_timeout';
  const nameOk = name.trim().length > 0;
  const codeOk = /^\d{6}$/.test(code);

  const doCreate = () => {
    if (!nameOk || connecting) return;
    saveName(name.trim());
    mpClient.createRoom(name.trim(), {
      maxSeats, smallBlind: Math.max(1, Math.floor(bb / 2)), bigBlind: bb, startingStack: stack, actionTimeoutSec: timeout,
    });
  };
  const doJoin = () => {
    if (!nameOk || !codeOk || connecting) return;
    saveName(name.trim());
    mpClient.joinRoom(code, name.trim());
  };

  return (
    <div className="mp-setup">
      <header className="mp-setup__head">
        <button className="mp-btn mp-btn--ghost" onClick={onBack} aria-label="返回">← 返回</button>
        <h1 className="mp-setup__title">多人对战</h1>
        <span className="mp-setup__sub">与好友同桌 · 房间号邀请</span>
      </header>

      {state.fatalError && (
        <div className="mp-alert" role="alert">
          ⚠ {state.fatalError}
          {/* 网络类失败会保留会话（BUG-R16-02），给出一键回到原房间的入口 */}
          {mpClient.hasResumableSession() && (
            <button className="mp-btn mp-btn--ghost mp-alert__retry" onClick={() => mpClient.resume()} disabled={connecting}>
              回到房间
            </button>
          )}
          {/*
            BUG-R18-01：连接类失败的头号成因是浏览器仍在运行旧版缓存包（旧包连不上房间服务）。
            给出可读提示 + 一键清缓存重载，用户无需手动清站点数据即可自救。
          */}
          {isConnFail && (
            <div className="mp-alert__hint" data-testid="mp-conn-hint">
              若此前打开过旧版页面，浏览器可能仍在使用旧缓存。可点「强制刷新」清理缓存后重试。
              <button className="mp-btn mp-btn--ghost mp-alert__retry" onClick={() => { void hardRefresh(); }}>
                强制刷新
              </button>
            </div>
          )}
        </div>
      )}

      <label className="mp-field">
        <span className="mp-field__label">你的昵称</span>
        <input
          className="mp-input" value={name} maxLength={16} placeholder="请输入昵称（最多 16 字）"
          onChange={(e) => setName(e.target.value)} aria-invalid={!nameOk}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); if (tab === 'join') doJoin(); else doCreate(); } }}
        />
        {!nameOk && <span className="mp-field__err">昵称不能为空</span>}
      </label>

      <Segmented
        value={tab}
        onChange={(v) => setTab(v as 'create' | 'join')}
        ariaLabel="创建或加入房间"
        options={[{ value: 'create', label: '创建房间' }, { value: 'join', label: '加入房间' }]}
      />

      {tab === 'create' ? (
        <div className="mp-panel">
          <div className="mp-grid2">
            <label className="mp-field">
              <span className="mp-field__label">座位上限</span>
              <select className="mp-input" value={maxSeats} onChange={(e) => setMaxSeats(Number(e.target.value))}>
                {[2, 3, 4, 5, 6].map((n) => <option key={n} value={n}>{n} 人桌</option>)}
              </select>
            </label>
            <label className="mp-field">
              <span className="mp-field__label">大盲注</span>
              <input className="mp-input" type="number" min={2} value={bb} onChange={(e) => setBb(Math.max(2, Number(e.target.value) || 2))} />
            </label>
            <label className="mp-field">
              <span className="mp-field__label">带入筹码</span>
              <input className="mp-input" type="number" min={bb * 2} value={stack} onChange={(e) => setStack(Math.max(bb * 2, Number(e.target.value) || bb * 2))} />
            </label>
            <label className="mp-field">
              <span className="mp-field__label">行动时限（秒，0=不限）</span>
              <input className="mp-input" type="number" min={0} value={timeout} onChange={(e) => setTimeoutSec(Math.max(0, Number(e.target.value) || 0))} />
            </label>
          </div>
          <button className="mp-btn mp-btn--primary mp-btn--block" disabled={!nameOk || connecting} onClick={doCreate}>
            {btnLabel('创建房间并入座')}
          </button>
          {connecting && <ConnectProgress stage={stage} />}
          <p className="mp-hint">小盲注自动取大盲的一半（{Math.max(1, Math.floor(bb / 2))}）。创建后会得到一个 6 位房间号，分享给好友即可加入。</p>
        </div>
      ) : (
        <div className="mp-panel">
          <label className="mp-field">
            <span className="mp-field__label">房间号</span>
            <input
              ref={codeRef} autoFocus
              className="mp-input mp-input--code" value={code} inputMode="numeric" maxLength={6}
              placeholder="6 位数字" onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); doJoin(); } }}
              aria-invalid={code.length > 0 && !codeOk}
              aria-label="房间号"
              enterKeyHint="go"
            />
            {code.length > 0 && !codeOk && <span className="mp-field__err">房间号必须是 6 位数字</span>}
          </label>
          <button className="mp-btn mp-btn--primary mp-btn--block" disabled={!nameOk || !codeOk || connecting} onClick={doJoin}>
            {btnLabel('加入房间')}
          </button>
          {connecting && <ConnectProgress stage={stage} />}
          <p className="mp-hint">向房主要一个 6 位房间号后填入即可入座。房间已满 / 已开局 / 不存在时会有明确提示。</p>
        </div>
      )}
    </div>
  );
}

/**
 * 首连二级进度（第 19 轮）：房间服务是 Durable Object，冷启动可能要几秒。
 * 只给「正在连接…」会让用户以为卡死；这里把过程拆成三步并高亮当前步，
 * 纯展示组件，不参与任何连接时序判定。
 */
function ConnectProgress({ stage }: { stage: MpConnectStage }) {
  const idx = MP_CONNECT_STEPS.findIndex((s) => s.stage === stage);
  const current = idx < 0 ? 0 : idx;
  return (
    <ol className="mp-steps" role="status" aria-live="polite" data-testid="mp-connect-progress" data-stage={stage}>
      {MP_CONNECT_STEPS.map((s, i) => {
        const st = i < current ? 'done' : i === current ? 'active' : 'todo';
        return (
          <li key={s.stage} className={`mp-steps__item is-${st}`} data-state={st}>
            <span className="mp-steps__dot" aria-hidden="true">{i < current ? '✓' : i + 1}</span>
            <span className="mp-steps__label">{s.label}</span>
          </li>
        );
      })}
    </ol>
  );
}
